'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  order_position: number;
  is_active: boolean;
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [faqItems, setFaqItems] = useState<FAQItem[]>([]);
  const [content, setContent] = useState<FAQContent>({
    title: 'Najczęstsze pytania.',
  });
  const supabase = createClient();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Fetch FAQ title from settings
        const { data: settingsData, error: settingsError } = await supabase
          .from('settings')
          .select('key, value')
          .in('key', ['faq_title']);

        if (settingsError) {
          console.error('Error fetching faq content:', settingsError.message);
        } else {
          const settingsMap: Record<string, string> = {};
          settingsData?.forEach((setting) => {
            settingsMap[setting.key] = setting.value;
          });

          setContent({
            title: settingsMap['faq_title'] || content.title,
          });
        }

        // Fetch FAQ items from database
        const { data: faqData, error: faqError } = await supabase
          .from('faq_items')
          .select('*')
          .eq('is_active', true)
          .order('order_position', { ascending: true });

        if (faqError) {
          console.error('Error fetching FAQ items:', faqError.message);
        } else {
          setFaqItems(faqData || []);
        }
      } catch (err) {
        console.error('Error:', err);
      }
    };

    fetchContent();
  }, [supabase]);

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <section id="faq" className="py-20 md:py-28 bg-offwhite">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-14 space-y-3">
          <p className="section-label">FAQ</p>
          <h2 className="text-4xl md:text-5xl font-900 tracking-tight text-navy leading-[0.95]">
            {content.title}
          </h2>
        </div>

        {/* FAQ list */}
        <div>
          {faqItems.map((item, i) => (
            <FAQItem
              key={item.id}
              item={item}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQItem({
  item,
  index,
  isOpen,
  onToggle,
}: {
  item: FAQItem;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const { elementRef, isVisible } = useScrollAnimation();

  return (
    <div
      ref={elementRef}
      className={`faq-item transition-all duration-700 ease-out ${
        isVisible
          ? 'opacity-100 translate-y-0' :'opacity-0 translate-y-8'
      }`}
      style={{
        transitionDelay: `${index * 100}ms`,
      }}
    >
      <button
        className="faq-question"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${index}`}
      >
        <span className="pr-4">{item.question}</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          className={`faq-icon ${isOpen ? 'open' : ''} text-blue-DEFAULT`}
          aria-hidden="true"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>
      <div
        id={`faq-answer-${index}`}
        className={`faq-answer ${isOpen ? 'open' : ''}`}
        role="region"
      >
        <p className="text-base text-muted font-500 leading-relaxed">
          {item.answer}
        </p>
      </div>
    </div>
  );
}